import axios from 'axios';


let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
API_BASE_URL += '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    console.log('API Request Interceptor - Token exists?', !!token);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Token added to request headers');
    } else {
      console.log('No token found for request');
    }

    console.log('Request URL:', config.url);
    console.log('Request Headers:', config.headers);

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });

    if (error.response?.status === 403) {
      console.log('403 Forbidden - Clearing auth data');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (userData: any) => api.post('/auth/register/', userData),
  login: (credentials: any) => {
    console.log('Login API called with:', credentials.username);
    return api.post('/auth/login/', credentials);
  },
  googleLogin: (token: string) => api.post('/auth/google-login/', { token }),
  staffLogin: (accessKey: string) => {
    console.log('Staff Login API called');
    return api.post('/auth/staff-login/', { access_key: accessKey });
  },
  logout: () => api.post('/auth/logout/'),
  getProfile: () => api.get('/auth/profile/'),
  updateProfile: (profileData: any) => api.patch('/auth/profile/update/', profileData),
  changePassword: (passwordData: any) => api.post('/auth/profile/change-password/', passwordData),
};

export const dashboardAPI = {
  getMetrics: () => {
    console.log('Dashboard API called');
    return api.get('/dashboard/');
  },
  getAttentionList: () => api.get('/dashboard/attention-list/'),
};

export const projectAPI = {
  getProjects: () => api.get('/projects/'),
  createProject: (projectData: any) => api.post('/projects/', projectData),
  updateProject: (id: number, projectData: any) => api.put(`/projects/${id}/`, projectData),
  deleteProject: (id: number) => api.delete(`/projects/${id}/`),
};

export const paymentAPI = {
  getPayments: () => api.get('/payments/'),
  getPaymentHistory: () => api.get('/payments/history/'),
  deletePaymentHistory: (id: number) => api.delete(`/payments/history/${id}/`),
  createPayment: (paymentData: any) => api.post('/payments/', paymentData),
  updatePayment: (id: number, paymentData: any) => api.put(`/payments/${id}/`, paymentData),
  deletePayment: (id: number) => api.delete(`/payments/${id}/`),
  getStaffPayments: () => api.get('/staff-payments/'),
  createStaffPayment: (paymentData: any) => api.post('/staff-payments/', paymentData),
  approveStaffPayment: (id: number) => api.post(`/staff-payments/${id}/approve/`),
  rejectStaffPayment: (id: number) => api.post(`/staff-payments/${id}/reject/`),
};

export const scheduleAPI = {
  getSchedules: () => api.get('/schedules/'),
  getCalendarView: () => api.get('/schedules/calendar/'),
  createSchedule: (scheduleData: any) => api.post('/schedules/', scheduleData),
  updateSchedule: (id: number, scheduleData: any) => api.put(`/schedules/${id}/`, scheduleData),
  deleteSchedule: (id: number) => api.delete(`/schedules/${id}/`),
  rescheduleSession: (id: number, scheduleData: any) => api.patch(`/schedules/${id}/reschedule/`, scheduleData),
};

export const studioRoomAPI = {
  getRooms: () => api.get('/studio-rooms/'),
  createRoom: (roomData: any) => api.post('/studio-rooms/', roomData),
  updateRoom: (id: number, roomData: any) => api.put(`/studio-rooms/${id}/`, roomData),
  deleteRoom: (id: number) => api.delete(`/studio-rooms/${id}/`),
};

export const staffAPI = {
  getStaff: () => api.get('/auth/directory/'),
  createStaff: (producerData: any) => api.post('/auth/directory/', producerData),
  updateStaff: (id: number, producerData: any) => api.patch(`/auth/directory/${id}/`, producerData),
  deleteStaff: (id: number) => api.delete(`/auth/directory/${id}/`),
  regenerateAccessKey: (id: number) => api.post(`/auth/directory/${id}/regenerate-key/`),
};

export const expenseAPI = {
  getExpenses: () => api.get('/expenses/'),
  createExpense: (expenseData: any) => api.post('/expenses/', expenseData),
  updateExpense: (id: number, expenseData: any) => api.put(`/expenses/${id}/`, expenseData),
  deleteExpense: (id: number) => api.delete(`/expenses/${id}/`),
};

export const notificationAPI = {
  getNotifications: () => api.get('/auth/notifications/'),
  getUnreadCount: () => api.get('/auth/notifications/unread-count/'),
  markAsRead: (id: number) => api.post(`/auth/notifications/${id}/mark-read/`),
  markAllAsRead: () => api.post('/auth/notifications/mark-all-read/'),
};

const superAdminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

superAdminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('super_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

superAdminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('super_access_token');
      localStorage.removeItem('super_refresh_token');
      localStorage.removeItem('super_user');
      window.location.href = '/super-admin/login';
    }
    return Promise.reject(error);
  }
);

export const superAdminAPI = {
  login: (credentials: any) => superAdminApi.post('/auth/super-admin/login/', credentials),
  getStudioOwners: () => superAdminApi.get('/auth/super-admin/studio-owners/'),
  createStudioOwner: (data: any) => superAdminApi.post('/auth/super-admin/studio-owners/', data),
  updateStudioOwner: (id: number, data: any) => superAdminApi.patch(`/auth/super-admin/studio-owners/${id}/`, data),
  deleteStudioOwner: (id: number) => superAdminApi.delete(`/auth/super-admin/studio-owners/${id}/`),
  getStaff: () => superAdminApi.get('/auth/super-admin/staff/'),
  createStaff: (data: any) => superAdminApi.post('/auth/super-admin/staff/', data),
  updateStaff: (id: number, data: any) => superAdminApi.patch(`/auth/super-admin/staff/${id}/`, data),
  deleteStaff: (id: number) => superAdminApi.delete(`/auth/super-admin/staff/${id}/`),
  getProjects: () => superAdminApi.get('/super-admin/projects/'),
  createProject: (data: any) => superAdminApi.post('/super-admin/projects/', data),
  updateProject: (id: number, data: any) => superAdminApi.put(`/super-admin/projects/${id}/`, data),
  deleteProject: (id: number) => superAdminApi.delete(`/super-admin/projects/${id}/`),
  getPayments: () => superAdminApi.get('/super-admin/payments/'),
  createPayment: (data: any) => superAdminApi.post('/super-admin/payments/', data),
  updatePayment: (id: number, data: any) => superAdminApi.put(`/super-admin/payments/${id}/`, data),
  deletePayment: (id: number) => superAdminApi.delete(`/super-admin/payments/${id}/`),
  getStaffPayments: () => superAdminApi.get('/super-admin/staff-payments/'),
  getExpenses: () => superAdminApi.get('/super-admin/expenses/'),
  createExpense: (data: any) => superAdminApi.post('/super-admin/expenses/', data),
  updateExpense: (id: number, data: any) => superAdminApi.put(`/super-admin/expenses/${id}/`, data),
  deleteExpense: (id: number) => superAdminApi.delete(`/super-admin/expenses/${id}/`),
};

export default api;
